import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Choice, Icon, Reason, useToast } from '@efira/ui';

import { useCheckout, usePlan, useProInterest } from '../../api/queries';
import type { BillingPeriod } from '../../api/queries';
import { minutes as minutesWord } from '../../lib/plural';
import { zoneLabel } from '../../lib/timezones';
import { LANDING_URL } from '../../config';
import { ModalFooter } from './ConsoleModal';
import { PLAN_NAME, ProStatus, usePlanState } from '../ProStatus';

/**
 * Окно тарифа: цена настоящая, чекаут - честная заглушка.
 *
 * ЦЕНА НЕ НАБРАНА ЗДЕСЬ НИ ОДНОЙ ЦИФРОЙ. Она приходит с сервера из
 * `world/pricing.ts` - того же места, откуда её возьмёт платёжный провайдер.
 * Цифра, набранная в разметке, расходится со списанием молча, и узнаём мы об
 * этом из возврата денег.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И НЕ БУДЕТ (`docs/DIVERGENCE.md`, 8): метки «популярный
 * выбор» при одном платном тарифе, старой зачёркнутой цены, обратного
 * отсчёта акции. Выгода годовой оплаты показана - но она ПОСЧИТАНА из двух
 * настоящих цен, а не написана рядом с ними.
 *
 * ЧЕТЫРЕ КОНТЕКСТА ВЫЗОВА - четыре заголовка и четыре пояснения. Человек
 * приходит сюда из конкретного места и с конкретным вопросом: из полосы
 * квоты - «сколько у меня осталось», из платного виджета - «почему он
 * платный». Один заголовок на все случаи отвечает только на первый.
 */
export type PlanContext = 'quota' | 'billing' | 'media' | 'watermark';

/** Контекст вызова из адреса. Неизвестный - «Ваш тариф». */
export function planContext(asked: string | null): PlanContext {
  return asked === 'billing' || asked === 'media' || asked === 'watermark' ? asked : 'quota';
}

export const PLAN_CONTEXTS: Record<PlanContext, { title: string; hint: string }> = {
  quota: {
    title: 'Ваш тариф',
    hint: 'Сколько эфира осталось сегодня и что даёт Pro.',
  },
  billing: {
    /* Не «Перейти на Pro»: перейти нельзя, ниже прямо написано, что оплата
       ещё подключается. Заголовок называет содержимое, призыв - на кнопке. */
    title: 'Что даёт Pro',
    /* Не «когда его можно будет купить»: цена в этом же окне уже названа,
       и две правды на одном экране - худшее, что может быть в разговоре о
       деньгах (находка ревью текстов). */
    hint: 'Чем Pro отличается от бесплатного и сколько стоит.',
  },
  media: {
    title: 'Медиа — виджет тарифа Pro',
    /* Главное - первой строкой: включить нельзя ВООБЩЕ, а не «нужно
       купить Pro». Человек пришёл сюда, чтобы включить виджет. */
    hint: 'Этот виджет входит в Pro. Включить его пока нельзя: оплата ещё подключается.',
  },
  watermark: {
    title: 'Знак платформы и Pro',
    hint: 'На бесплатном тарифе знак на оверлее остаётся — это условие бесплатного доступа. В Pro его нет.',
  },
};

function when(iso: string | null): string {
  return iso === null
    ? 'в полночь по вашему времени'
    : new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function Plan() {
  const [params] = useSearchParams();
  const plan = usePlan();
  const interest = useProInterest();
  const checkout = useCheckout();
  const toast = useToast();
  /* Год выбран по умолчанию - как и у эталона. Это не приём: выгода
     посчитана и показана, а месяц в одном нажатии. */
  const [period, setPeriod] = useState<BillingPeriod>('year');
  const [refused, setRefused] = useState<string | null>(null);
  const state = usePlanState();

  const context = planContext(params.get('from'));
  const copy = PLAN_CONTEXTS[context];

  const data = plan.data;
  const daily = data?.daily;
  const chosen = data?.price.periods.find((line) => line.period === period);

  return (
    <div data-plan="root" className="flex flex-col gap-4">
      {/*
        ЗАГОЛОВОК ОДИН - ОН В РАМЕ ОКНА.
        Было три строки об одном подряд: «Тариф», «Что даёт Pro»,
        «Возможности Pro и сколько он стоит» (находка владельца). Заголовок
        контекста переехал в раму окна (`routes.tsx`), здесь осталась одна
        строка пояснения - `plan.context` реестра.
      */}
      <p data-plan="context" className="text-foreground/80 text-sm">
        {copy.hint}
      </p>

      {/* Расход времени - ТОЛЬКО в контексте «Ваш тариф»: человеку, пришедшему
          из платного виджета, остаток минут не отвечает ни на что. */}
      {context === 'quota' && daily !== null && daily !== undefined && (
        <section
          data-plan="quota"
          className="border-foreground/10 rounded-lg flex flex-col gap-1 border p-3"
        >
          <span className="text-foreground/80 text-xs tracking-wide uppercase">
            Эфир в эти сутки
          </span>
          {/* ОСТАТОК, а не расход: окно обещает ответить, сколько осталось,
              и «0 минут из 60» отвечало на это наоборот - читалось как
              исчерпанный лимит. */}
          <span className="text-foreground text-xl font-semibold tabular-nums">
            {daily.limit === null || daily.remaining === null
              ? `${minutesWord(daily.used)} в эфире, дневного лимита нет`
              : `Осталось ${minutesWord(daily.remaining)} из ${String(daily.limit)}`}
          </span>
          {/* Сброс - в ЧАСОВОМ ПОЯСЕ ПОЛЬЗОВАТЕЛЯ, и пояс НАЗВАН: полночь
              UTC в Душанбе приходится на пять утра, то есть на середину
              ночного эфира, а проверить, ту ли зону мы взяли, человек
              может только увидев её. */}
          <span className="text-foreground/90 text-sm">
            Сброс в {when(daily.resetAt)},{' '}
            {data?.timezone === undefined ? 'ваш часовой пояс' : zoneLabel(data.timezone)}.
            Симулятор это время не расходует.
          </span>
        </section>
      )}

      {/* Таблица и кнопка - ОДНА ГРУППА во вложенной карточке: иначе кнопка
          висит отдельно от того, к чему относится. */}
      {/*
        НА УЗКОМ ЭКРАНЕ - ДВЕ КАРТОЧКИ ПОДРЯД, А НЕ ТАБЛИЦА (N-14). Три
        колонки на 390 px срезали сторону Pro: «Без дневного л…», «10
        аккаунт…». Сравнение то же, только каждый тариф - своим столбцом
        сверху вниз. Таблица - с ширины `sm`.
      */}
      <div data-plan="cards" className="flex flex-col gap-3 sm:hidden">
        {(
          [
            ['free', PLAN_NAME.free],
            ['pro', PLAN_NAME.pro],
          ] as const
        ).map(([id, name]) => (
          <section
            key={id}
            data-plan-card={id}
            className="border-foreground/10 flex flex-col gap-1 rounded-lg border p-4"
          >
            <p className="text-foreground flex items-center gap-2 text-sm font-semibold">
              {id === 'pro' && <Icon name="crown" size="sm" tone="warning" />}
              {name}
              {data?.current.id === id && <Current mark={false} />}
            </p>
            {(data?.lines ?? []).map((line) => (
              <p key={line.source} className="text-foreground/90 text-sm">
                <span className="text-foreground/80">{line.title}: </span>
                <span className={id === 'pro' ? 'text-foreground font-semibold' : ''}>
                  {id === 'pro' ? line.pro : line.free}
                </span>
              </p>
            ))}
          </section>
        ))}
      </div>

      <section
        data-plan="tables"
        className="border-foreground/10 hidden flex-col gap-2 rounded-lg border p-4 sm:flex"
      >
        <div className="text-foreground/80 border-foreground/10 grid grid-cols-[1fr_9.5rem_9.5rem] items-end gap-x-4 border-b pb-2 text-xs tracking-wide uppercase">
          <span>Возможности</span>
          <span data-plan="free-table" className="flex items-center gap-2">
            {PLAN_NAME.free}
            {/* Состояние, а не цена: окно обязано отвечать на первый вопрос
                «а у меня сейчас что». Метка стоит у ТОГО тарифа, который
                у человека сейчас, - на Pro она переезжает во вторую
                колонку, иначе окно отвечает неправду тем, кто уже платит. */}
            {/* Не `badge-soft`: в тёмной теме он даёт контраст ниже AA -
                тот же случай, что у чипа категории в смене игры. */}
            {data?.current.id === 'free' && <Current />}
          </span>
          <span data-plan="pro-table" className="text-foreground flex items-center gap-1">
            <Icon name="crown" size="sm" tone="warning" />
            Pro
            {data?.current.id === 'pro' && <Current />}
          </span>
        </div>

        {(data?.lines ?? [])
          .filter((line) => line.same !== true)
          .map((line) => (
            <div
              key={line.source}
              data-plan-line={line.source}
              /* Без линии под каждой строкой: разлиновка превращает сравнение
               в таблицу данных. Строки разводит вес, а не рамки. */
              className="grid grid-cols-[1fr_9.5rem_9.5rem] items-baseline gap-x-4 py-1 text-sm"
            >
              <span className="text-foreground/90 flex flex-col">
                <span className="font-medium">{line.title}</span>
                {line.same === true && (
                  <span className="text-foreground/80 text-xs">одинаково в обоих тарифах</span>
                )}
              </span>
              <span data-plan-free className="text-foreground/80">
                {line.free}
              </span>
              {/* Сторона Pro - единственный акцент таблицы: к ней зовут. */}
              <span data-plan-pro className="text-foreground font-semibold">
                {line.pro}
              </span>
            </div>
          ))}

        {/* Одинаковое на обоих тарифах - НЕ строкой сравнения: строка, где
            нечего сравнивать, заставляет перечитывать её трижды. */}
        {(data?.lines ?? [])
          .filter((line) => line.same === true)
          .map((line) => (
            <p
              key={line.source}
              data-plan-line={line.source}
              className="text-foreground/80 border-foreground/10 border-t pt-2 text-sm"
            >
              {line.title} одинаковы на обоих тарифах: <span data-plan-free>{line.free}</span>
              <span className="sr-only" data-plan-pro>
                {line.pro}
              </span>
            </p>
          ))}
      </section>

      {/*
        КАРТОЧКА PRO: цена, период, оформление (`plan.card` реестра).

        Один платный тариф - одна карточка. Метки «популярный выбор» здесь
        нет: сравнивать не с чем, а метка сравнения без сравнения - приём.
      */}
      <section
        data-plan="card"
        className="border-primary/40 bg-muted rounded-lg flex flex-col gap-3 border p-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-foreground flex items-center gap-2 text-base font-semibold">
            <Icon name="crown" size="sm" tone="warning" />
            Pro
          </span>

          {/* Переключатель периода (`plan.period` реестра). Сегментами, а не
              списком: два значения, и оба должны быть видны сразу. */}
          <Choice
            label="Период оплаты"
            data-plan="period"
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'month', label: 'Месяц', data: { 'data-plan-period': 'month' } },
              {
                value: 'year',
                label: (
                  <>
                    Год
                    {data?.price !== undefined && (
                      /* Выгода видна ДО переключения, на самом сегменте:
                         иначе человек выбирает период, не зная, что год
                         дешевле. */
                      <span className="ms-1">−{String(data.price.saving.percent)} %</span>
                    )}
                  </>
                ),
                data: { 'data-plan-period': 'year' },
              },
            ]}
          />
        </div>

        {/* ЦЕНА ПРИХОДИТ С СЕРВЕРА. Ни одной цифры в разметке. */}
        <p className="flex flex-col gap-1">
          <span className="flex items-baseline gap-2">
            {/* Цена - главное число карточки: базовым цветом и крупно.
                Зелёный остаётся кнопке, иначе два акцента спорят. */}
            <span data-plan="price" className="text-foreground text-4xl font-semibold">
              {chosen?.text ?? '—'}
            </span>
            <span className="text-foreground/80 text-sm">
              {period === 'year' ? 'в год' : 'в месяц'}
            </span>
          </span>
          {/* «В пересчёте»: списания в 3,50 $ не существует ни при каком
              периоде, и «это 3,50 $ в месяц» читается как оно. */}
          {period === 'year' && (
            <span data-plan="per-month" className="text-foreground/90 text-sm">
              в пересчёте на месяц — {chosen?.perMonthText ?? '—'}
            </span>
          )}
        </p>

        {/* Выгода ПОСЧИТАНА сервером из двух цен, а не написана рядом. */}
        {period === 'year' && data?.price !== undefined && (
          <p data-plan="saving" className="text-foreground/90 text-sm">
            Дешевле на {String(data.price.saving.percent)} % — экономия {data.price.saving.text} по
            сравнению с помесячной оплатой.
          </p>
        )}

        {/*
          КНОПКА - В ПОДВАЛЕ ОКНА, А НЕ В КАРТОЧКЕ ЦЕНЫ.

          Находка аудита единицы 16 (09): на экране 900 px «Оформить Pro»
          оказывалась ниже видимой области - окно открывали ради действия,
          которого не видно. Подвал закреплён, прокручивается только тело.

          ПОКА ОПЛАТЫ НЕТ - КНОПКА НЕ ГЛАВНАЯ И НАЗВАНА ЧЕСТНО (N-11). Было:
          зелёная «Оформить Pro», отказ после нажатия, а строка «оформить
          пока нельзя» стояла в теле и на 1470 уходила под этот же подвал.
          Теперь причина - рядом с кнопкой, в подвале, а кнопка делает то,
          что сейчас возможно: отмечает, что человеку написать, когда оплата
          заработает. Слово о состоянии - из одного источника (`ProStatus`).
        */}
        <ModalFooter>
          {state.selling ? (
            <Button
              variant="primary"
              className="w-full sm:w-auto"
              data-plan="checkout"
              loading={checkout.isPending}
              onClick={() => {
                setRefused(null);
                checkout.mutate(period, {
                  onSuccess: (result) => {
                    if (result.ok && typeof result.url === 'string') {
                      window.location.assign(result.url);

                      return;
                    }

                    /* Отказ - не тост об ошибке: человеку нужен не
                       «попробуйте позже», а объяснение и способ узнать,
                       когда оплата появится. */
                    setRefused(result.message ?? 'Оплата ещё не подключена.');
                  },
                });
              }}
            >
              Оформить Pro
            </Button>
          ) : (
            <div className="flex w-full flex-wrap items-center justify-end gap-2">
              <Reason id="plan-checkout-why" data-plan="not-selling" className="min-w-0 flex-1">
                <ProStatus /> Оформить Pro пока нельзя. Цена выше действует на сегодня; если
                изменим, скажем до запуска оплаты.
                {data?.interestedAt !== null && data?.interestedAt !== undefined && (
                  <span data-plan="interested">
                    {' '}
                    Отмечено {new Date(data.interestedAt).toLocaleDateString('ru-RU')} — напишем на
                    почту.
                  </span>
                )}
              </Reason>
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                data-plan="checkout"
                loading={interest.isPending}
                disabled={data?.interestedAt !== null && data?.interestedAt !== undefined}
                aria-describedby="plan-checkout-why"
                onClick={() => {
                  interest.mutate(undefined, {
                    onSuccess: () => {
                      toast.show({
                        tone: 'success',
                        title: 'Записали',
                        text: 'Напишем на вашу почту, когда Pro можно будет включить.',
                      });
                    },
                  });
                }}
              >
                <Icon name="bell" size="sm" />
                {data?.interestedAt !== null && data?.interestedAt !== undefined
                  ? 'Напишем, когда появится оплата'
                  : 'Написать мне, когда появится оплата'}
              </Button>
            </div>
          )}
        </ModalFooter>

        {refused !== null && (
          <div
            data-plan="checkout-stub"
            className="border-foreground/15 rounded-lg flex flex-col gap-2 border border-dashed p-3"
          >
            <p className="text-foreground text-sm font-medium">Оплата ещё подключается</p>
            <p className="text-foreground/90 text-sm">
              Списать с вас нечего: платёжный сервис ещё не подключён. Отметим вашу почту и напишем,
              когда оплату можно будет пройти.
            </p>

            <Button
              variant="secondary"
              size="s"
              className="self-start"
              data-plan="cta"
              disabled={interest.isPending || data?.interestedAt !== null}
              onClick={() => {
                interest.mutate(undefined, {
                  onSuccess: () => {
                    toast.show({
                      tone: 'success',
                      title: 'Записали',
                      text: 'Напишем на вашу почту, когда Pro можно будет включить.',
                    });
                  },
                });
              }}
            >
              <Icon name="bell" size="sm" />
              Написать мне, когда появится оплата
            </Button>

            {data?.interestedAt !== null && data?.interestedAt !== undefined && (
              <span data-plan="interested" className="text-foreground/90 text-sm">
                Отмечено {new Date(data.interestedAt).toLocaleDateString('ru-RU')} — напишем на
                почту.
              </span>
            )}
          </div>
        )}

        {/* УСЛОВИЯ - ССЫЛКАМИ, И КАЖДАЯ ОТКРЫВАЕТСЯ. Пустая ссылка
            «Условия» хуже отсутствующей: человек нажимает её именно тогда,
            когда решает, платить ли. Тексты пишет владелец; страницы уже
            есть и помечены как черновик. */}
        {/* НЕ «вы соглашаетесь»: документы пока черновики, и согласия с
            несуществующим текстом взять нельзя. */}
        <p data-plan="legal" className="text-foreground/80 text-sm">
          <Legal to="/legal/subscription">Условия подписки</Legal>,{' '}
          <Legal to="/legal/refunds">возврат</Legal> и{' '}
          <Legal to="/legal/cancellation">отмена</Legal> — готовятся, сейчас это черновики. Отменить
          подписку можно будет в любой момент: доступ доработает до конца оплаченного периода.
        </p>
      </section>
    </div>
  );
}

/** Метка «ваш» у тарифа, который действует сейчас. */
function Current({ mark = true }: { mark?: boolean }) {
  return (
    <span
      /* Пометка - у метки таблицы: карточки узкого экрана повторяют её, и
         два узла с одной пометкой сделали бы её поиск неоднозначным. */
      data-plan={mark ? 'current' : undefined}
      className="border-foreground/20 text-foreground/90 rounded-full border px-2 py-1 text-xs font-normal normal-case"
    >
      ваш
    </span>
  );
}

/**
 * Ссылка на правовой документ.
 *
 * Документы живут НА САЙТЕ, а не в консоли: их читают и до регистрации, и
 * без входа. Открывается в новой вкладке - уходить из окна оплаты нельзя:
 * вернуться в него нечем.
 */
function Legal({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <a
      data-plan-legal={to}
      href={`${LANDING_URL}${to}`}
      target="_blank"
      rel="noreferrer"
      className="text-foreground underline"
    >
      {children}
    </a>
  );
}
